/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren, ComponentProps, Ref } from 'preact';
import { forwardRef, useImperativeHandle, useRef } from 'preact/compat';

import { FocusableHandle } from '../hooks/UNSAFE_useFocusableTextField';
import { useCollectionFocusRing } from '../hooks/PRIVATE_useCollectionFocusRing';
import { FlexboxProps } from '../utils/UNSAFE_interpolations/flexbox';
import { Size } from '../utils/UNSAFE_size';

import { Flex } from '../UNSAFE_Flex';
import { Label } from '../UNSAFE_Label';

import {
  InlineHelp,
  InlineUserAssistance,
  InlineUserAssistanceContainer,
  UserAssistanceDensityType
} from '../UNSAFE_UserAssistance';
import { useCheckboxRadioField } from './useCheckboxRadioField';

import { xUnits } from '../UNSAFE_Theme';

import { topUAContainerStyles } from './CheckboxRadioFieldStyles.css';
import { LabelValueLayout } from '../UNSAFE_LabelValueLayout';
import { CheckboxRadioContext } from './CheckboxRadioContext';

// FIXME: 
type CalcType = `calc(${string})`;

type InlineUserAssistanceProps = ComponentProps<typeof InlineUserAssistance>;

type Props = {
  /**
   * The ID of an element (or space separated IDs of multiple elements) that
   * describes the input.
   */
  'aria-describedby'?: string;
  /**
   * Text to provide guidance to help the user understand what data to enter.
   */
  assistiveText?: InlineUserAssistanceProps['assistiveText'];
  /**
   * baseId, used to generate other element IDs.
   */
  baseId: string;
  /**
   * <RadioOption /> or <Checkbox />
   */
  children: ComponentChildren;
  /**
   * layout direction of the children elements
   */
  direction?: FlexboxProps['direction'];
  /**
   * Value of the radio option.
   */
  hasValue?: boolean;
  /**
   * Help source URL associated with the component.
   */
  helpSourceLink?: InlineUserAssistanceProps['helpSourceLink'];
  /**
   * Custom text to be rendered for the <code>helpSourceLink</code>.
   */
  helpSourceText?: InlineUserAssistanceProps['helpSourceText'];
  /**
   * Specifies if the radio group is disabled.
   */
  isDisabled?: boolean;
  /**
   * Specifies if the radio group is read-only.
   */
  isReadonly?: boolean;
  /**
   * Specifies whether the radio group requires a selection to be made.
   */
  isRequired?: boolean;
  /**
   * Label text for the group of radio buttons.
   */
  label: string;
  /**
   * Specifies where the label is positioned relative to the field.
   */
  labelEdge?: 'none' | 'top' | 'start' | 'inside';
  /**
   * Specifies the width of the label when <code>labelEdge</code> is <code>"start"</code>.
   */
  labelStartWidth?: Size;
  /**
   * Messages to show on screen that are associated with the component.
   */
  messages?: InlineUserAssistanceProps['messages'];
  // TODO: future work: extend this to support checkbox usage too
  role?: 'radiogroup';
  /**
   * Specifies the density of the user assistance presentation.  It can be set to:
   * <ul>
   * <li><code>'efficient'</code>: Show inline and reserve space to prevent layout reflow when user
   * assistance text is displayed.</li>
   * <li><code>'reflow'</code>: Show inline.  Layout will reflow when text is displayed.</li>
   * <li><code>'compact'</code>: Messages, help, hints, and required will not be shown inline; they will show in a mode that keeps the screen more compact, like a popup for the messages, and a required icon to indicate Required. </li>
   * </ul>
   */
  userAssistanceDensity?: UserAssistanceDensityType;

  parentComponentVariant: 'radioGroup';
};

export const CheckboxRadioField = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      assistiveText,
      baseId,
      direction = 'column',
      hasValue,
      helpSourceLink,
      helpSourceText,
      isDisabled,
      isReadonly,
      isRequired,
      label,
      labelEdge,
      labelStartWidth = '33%',
      parentComponentVariant,
      messages,
      userAssistanceDensity,
      role,
      children
    }: Props,
    ref: Ref<FocusableHandle> = null
  ) => {
    const hasTopUserAssistance = !!assistiveText || (!!helpSourceLink && !!helpSourceText);
    const hasBottomUserAssistance = !isDisabled && !isReadonly;
    const groupRef = useRef<HTMLDivElement>(null);

    const { groupProps, labelProps, topUserAssistanceProps, bottomUserAssistanceProps } =
      useCheckboxRadioField({
        ariaDescribedBy,
        baseId,
        isRequired,
        isReadonly,
        label,
        labelEdge,
        messages,
        hasValue,
        hasTopUserAssistance,
        hasBottomUserAssistance
      });

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (!isDisabled) {
          const inputChildren = groupRef.current?.querySelectorAll<HTMLInputElement>('input') ?? [];
          const selectedChild = Array.from(inputChildren).find((child) => child.checked);

          if (selectedChild) {
            selectedChild.focus();
          } else {
            inputChildren[0].focus();
          }
        }
      },
      blur: () => {
        if (groupRef.current?.contains(document.activeElement)) {
          (document.activeElement as HTMLElement).blur();
        }
      }
    }));

    const [isFocusRingShown, focusRingProps] = useCollectionFocusRing(
      (elem: Element | null) => {
        return !!groupRef.current?.contains(elem);
      },
      ['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft']
    );

    const content = (
      <>
        {hasTopUserAssistance && (
          <div
            id={`${baseId}-top-ua-container`}
            class={labelEdge === 'start' ? topUAContainerStyles.start : topUAContainerStyles.top}>
            <InlineUserAssistanceContainer {...topUserAssistanceProps}>
              <InlineHelp
                assistiveText={assistiveText}
                sourceLink={helpSourceLink}
                sourceText={helpSourceText}
              />
            </InlineUserAssistanceContainer>
          </div>
        )}
        <Flex direction={direction} {...(direction === 'row' && { gap: xUnits(8) as CalcType })}>
          {children}
        </Flex>
        {hasBottomUserAssistance && !isReadonly && (
          <InlineUserAssistance
            {...bottomUserAssistanceProps}
            requiredAlignment="start"
            messages={messages}
            userAssistanceDensity={
              userAssistanceDensity === 'reflow' ? userAssistanceDensity : 'efficient'
            }
            id={`${baseId}-bottom-ua-container`}
          />
        )}
      </>
    );

    // TODO: spacing under label when labelEdge === 'top' and hasTopUA.
    return (
      <div role={role} ref={groupRef} {...groupProps} {...focusRingProps}>
        {labelEdge === 'none' && content}
        {labelEdge !== 'none' && (
          <LabelValueLayout
            parentComponentVariant={parentComponentVariant}
            hasTopUserAssistance={hasTopUserAssistance}
            label={
              <Label
                {...labelProps}
                variant={labelEdge}
                userAssistanceDensity="efficient"
                parentComponentVariant={parentComponentVariant}>
                {label}
              </Label>
            }
            labelEdge={labelEdge === 'inside' ? 'top' : labelEdge}
            labelStartWidth={labelStartWidth}>
            <CheckboxRadioContext.Provider value={{ isFocusRingShown }}>
              {content}
            </CheckboxRadioContext.Provider>
          </LabelValueLayout>
        )}
      </div>
    );
  }
);
